import React, { useEffect, useState, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Info, ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * MapModal - Modal pentru afișarea detaliilor hărții
 * SCROLL-TO-ZOOM pe imagine, scroll normal pe descriere
 */
const MapModal = ({ map, onClose, onNext, onPrev }) => {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [showHint, setShowHint] = useState(true);
  const isMouseOverImageRef = useRef(false);
  const imageContainerRef = useRef(null);
  const imageRef = useRef(null);
  const contentRef = useRef(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        onNext?.();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        onPrev?.();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose, onNext, onPrev]);

  // Reset zoom when map changes
  useEffect(() => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  }, [map?.id]);

  // Hide hint after 3 seconds
  useEffect(() => {
    const timer = setTimeout(() => setShowHint(false), 3000);
    return () => clearTimeout(timer);
  }, []);

  // Global wheel handler for zoom
  useEffect(() => {
    const handleGlobalWheel = (e) => {
      if (isMouseOverImageRef.current && imageContainerRef.current) {
        e.preventDefault();
        e.stopPropagation();
        
        const delta = e.deltaY > 0 ? 0.9 : 1.1;
        setScale(prev => {
          const newScale = prev * delta;
          if (newScale <= 1) {
            setPosition({ x: 0, y: 0 });
            return 1;
          }
          return Math.min(5, newScale);
        });
      }
    };

    window.addEventListener('wheel', handleGlobalWheel, { passive: false });
    return () => window.removeEventListener('wheel', handleGlobalWheel);
  }, []);

  // Mouse drag handlers
  const handleMouseDown = (e) => {
    if (scale > 1) {
      e.preventDefault();
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && scale > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Touch handlers for mobile
  const [lastTouchDistance, setLastTouchDistance] = useState(0);
  const [lastTapTime, setLastTapTime] = useState(0);
  const DOUBLE_TAP_DELAY = 300;

  const getTouchDistance = (touch1, touch2) => {
    const dx = touch1.clientX - touch2.clientX;
    const dy = touch1.clientY - touch2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  // Reset zoom function
  const resetZoom = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
    setIsDragging(false);
    setLastTouchDistance(0);
  };

  const handleTouchStart = (e) => {
    const now = Date.now();
    
    // Double-tap to reset
    if (e.touches.length === 1) {
      if (now - lastTapTime < DOUBLE_TAP_DELAY) {
        e.preventDefault();
        resetZoom();
        setLastTapTime(0);
        return;
      }
      setLastTapTime(now);
      
      if (scale > 1) {
        setIsDragging(true);
        setDragStart({ 
          x: e.touches[0].clientX - position.x, 
          y: e.touches[0].clientY - position.y 
        });
      }
    } else if (e.touches.length === 2) {
      e.preventDefault();
      setIsDragging(false);
      setLastTapTime(0);
      setLastTouchDistance(getTouchDistance(e.touches[0], e.touches[1]));
    }
  };

  const handleTouchMove = (e) => {
    setLastTapTime(0);
    
    if (e.touches.length === 1 && isDragging && scale > 1) {
      setPosition({
        x: e.touches[0].clientX - dragStart.x,
        y: e.touches[0].clientY - dragStart.y
      });
    } else if (e.touches.length === 2 && lastTouchDistance > 0) {
      e.preventDefault();
      const distance = getTouchDistance(e.touches[0], e.touches[1]);
      const scaleChange = distance / lastTouchDistance;
      
      setScale(prev => {
        const newScale = prev * scaleChange;
        if (newScale < 1) {
          setPosition({ x: 0, y: 0 });
          return 1;
        }
        return Math.min(5, newScale);
      });
      
      setLastTouchDistance(distance);
    }
  };

  const handleTouchEnd = (e) => {
    if (e.touches.length === 0) {
      setIsDragging(false);
      setLastTouchDistance(0);
    } else if (e.touches.length === 1) {
      if (scale > 1) {
        setIsDragging(true);
        setDragStart({
          x: e.touches[0].clientX - position.x,
          y: e.touches[0].clientY - position.y
        });
      }
      setLastTouchDistance(0);
    }
  };

  const handleTouchCancel = () => {
    setIsDragging(false);
    setLastTouchDistance(0);
    setLastTapTime(0);
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Process description text
  const processText = (text) => {
    if (Array.isArray(text)) {
      return text.map((line, i) => (
        <p key={i} className="mb-3 last:mb-0">{line}</p>
      ));
    }
    if (typeof text === 'string') {
      return text.split('\n\n').map((paragraph, i) => (
        <p key={i} className="mb-3 last:mb-0 whitespace-pre-line">{paragraph}</p>
      ));
    }
    return <p>{String(text || '')}</p>;
  };

  // Format year display
  const formatYear = (year) => {
    if (!year) return '';
    const yearStr = String(year);
    if (yearStr.includes('î.Hr') || yearStr.includes('d.Hr') || yearStr.includes('sec.')) {
      return yearStr;
    }
    return yearStr;
  };

  // Memoize description content - fullDescription contains everything including historical context
  const descriptionContent = useMemo(() => {
    const fullDesc = map.fullDescription || map.description || '';
    
    return (
      <>
        {fullDesc && (
          <div className="mb-4">
            {processText(fullDesc)}
          </div>
        )}
      </>
    );
  }, [map.fullDescription, map.description]);

  if (!map) return null;

  return createPortal(
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-2 md:p-4 bg-black/70 backdrop-blur-sm"
      onClick={handleOverlayClick}
    >
      <div 
        className="bg-[#faf8f5] rounded-xl shadow-2xl w-full max-w-5xl max-h-[95vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex-shrink-0 px-4 md:px-6 py-3 md:py-4 border-b border-gray-200 bg-gradient-to-r from-[#faf8f5] to-[#f5f3ef]">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <h2 className="text-lg md:text-xl lg:text-2xl font-bold text-gray-900 leading-tight">
                {map.title}
              </h2>
              {map.subtitle && (
                <p className="text-xs md:text-sm text-[#8B7355] mt-0.5">{map.subtitle}</p>
              )}
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-xs md:text-sm text-gray-600">
                <span className="font-medium">Autor: {map.author}</span>
                <span className="hidden sm:inline text-gray-300">|</span>
                <span>An: {formatYear(map.year)}</span>
              </div>
            </div>
            
            <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
              <button
                onClick={onPrev}
                className="p-1.5 md:p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                title="Harta anterioară (←)"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                onClick={onNext}
                className="p-1.5 md:p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                title="Harta următoare (→)"
              >
                <ChevronRight size={20} />
              </button>
              <button
                onClick={onClose}
                className="p-1.5 md:p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                title="Închide (Esc)"
              >
                <X size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* Content - Scrollable */}
        <div ref={contentRef} className="flex-1 overflow-y-auto overscroll-contain">
          {/* Image with zoom */}
          <div className="px-4 md:px-6 py-4">
            <div 
              ref={imageContainerRef}
              className="relative overflow-hidden rounded-lg border border-gray-300 shadow-md bg-[#f5f3ef]"
              style={{ 
                cursor: isDragging ? 'grabbing' : (scale > 1 ? 'grab' : 'zoom-in'),
                height: '50vh',
                minHeight: '300px'
              }}
              onMouseEnter={() => { isMouseOverImageRef.current = true; }}
              onMouseLeave={() => { isMouseOverImageRef.current = false; handleMouseUp(); }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onTouchCancel={handleTouchCancel}
            >
              <div 
                className="w-full h-full flex items-center justify-center"
                style={{ touchAction: 'none' }}
              >
                <img
                  ref={imageRef}
                  src={map.imageUrl || '/hero-bg-new.png'}
                  alt={map.title}
                  className="max-w-full max-h-full select-none"
                  style={{ 
                    transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
                    transformOrigin: 'center center',
                    transition: isDragging ? 'none' : 'transform 0.1s ease-out'
                  }}
                  onError={(e) => { e.target.src = '/hero-bg-new.png'; }}
                  draggable={false}
                />
              </div>

              {/* Hint */}
              {showHint && (
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 px-4 py-2 bg-gray-900/80 text-white text-xs rounded-lg pointer-events-none animate-pulse">
                  <span className="hidden md:inline">🖱️ Scroll pentru zoom • Trage pentru a muta</span>
                  <span className="md:hidden">👆 Pinch zoom • Double-tap reset</span>
                </div>
              )}

              {/* Zoom indicator with reset button */}
              {scale > 1 && (
                <button 
                  onClick={resetZoom}
                  className="absolute top-3 right-3 px-3 py-1.5 bg-white/95 hover:bg-white rounded-lg text-xs text-gray-700 shadow-md flex items-center gap-2 transition-colors"
                >
                  <span>{Math.round(scale * 100)}%</span>
                  <span className="text-gray-400">✕</span>
                </button>
              )}
            </div>
          </div>

          {/* Description */}
          <div className="px-4 md:px-6 pb-6">
            <div className="flex items-center gap-2 mb-3">
              <Info size={18} className="text-[#8B7355]" />
              <h3 className="font-semibold text-gray-800">Descriere</h3>
            </div>
            <div className="text-sm md:text-base text-gray-700 leading-relaxed">
              {descriptionContent}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default MapModal;
